import AttachedMoneyIcon from '@mui/icons-material/AttachMoneyOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { GridColDef, GridRowParams } from '@mui/x-data-grid/models';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
	getFutureAndGeneratedInvoicesQueryKey,
	getUseInvoiceQueryKey,
	usePayInvoiceMutation,
	useReverseInvoiceMutation,
	useWriteOffInvoiceMutation,
} from '../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../api/PolicyholderApi';
import {
	AuthData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	withAuthContext,
} from '../context/auth';
import { useCancellationContext } from '../context/CancellationProvider';
import { useEndorsementContext } from '../context/EndorsementProvider';
import { usePolicyContext } from '../context/policy';
import { usePremiumReportContext } from '../context/premiumReport';
import { useRenewalContext } from '../context/RenewalProvider';
import {
	INVOICES_TABLE__COPY_INVOICE_LABEL,
	INVOICES_TABLE__COPY_INVOICE_SUCCESS_TOAST,
	INVOICES_TABLE__DOWNLOAD_INVOICE_LABEL,
	INVOICES_TABLE__HEADER,
	INVOICES_TABLE__INVOICE_BILLING_PERIOD_HEADER,
	INVOICES_TABLE__INVOICE_DUE_DATE_HEADER,
	INVOICES_TABLE__INVOICE_ID_HEADER,
	INVOICES_TABLE__INVOICE_ISSUE_DATE_HEADER,
	INVOICES_TABLE__INVOICE_STATUS_HEADER,
	INVOICES_TABLE__INVOICE_TOTAL_DUE_HEADER,
	INVOICES_TABLE__INVOICE_TYPE_HEADER,
	INVOICES_TABLE__PAY_INVOICE_LABEL,
	INVOICES_TABLE__REVERSE_PAYMENT_LABEL,
	INVOICES_TABLE__WRITE_OFF_LABEL,
} from '../data/labelsAndMessages';
import { CurrencyCode } from '../types/currencyCode';
import {
	PaymentCreateRequest,
	PaymentResponse2,
	PaymentReversalReason,
	PolicyInvoice2Response,
	PolicyInvoice2StatusAndTime,
} from '../types/policy';
import {
	PremiumReportingInvoice,
	PremiumReportingInvoiceStatusAndTime,
	PremiumReportingPayment,
} from '../types/premiumReports';
import { formatCurrency } from '../util/util';
import { LinkCell } from './grid';
import GridWithToolbar from './grid/GridWithToolbar';
import InvoiceStateChip, { formatInvoiceStatus } from './InvoiceStateChip';
import PayInvoiceModal from './payments/components/PayInvoiceModal';
import ReversePaymentModal from './payments/components/ReversePaymentModal';
import WriteOffPaymentModal from './payments/components/WriteOffPaymentModal';
import { InvoiceTypeEnum } from './policy/InvoiceTypeEnum';
import { infoToast } from './Toast';

interface Props {
	policyholderLocator: string;
	invoices: PolicyInvoice2Response[] | PremiumReportingInvoice[];
	isLoading?: boolean;
	payInvoice?: (locator: string, request: PaymentCreateRequest) => void;
	authData: AuthData;
	currency?: CurrencyCode;
	useInvoiceCurrency?: boolean;
}

interface ModalInfo {
	open: boolean;
	invoice?: PolicyInvoice2Response | PremiumReportingInvoice;
}

interface InvoiceRow {
	locator: string;
	displayId: string;
	type: string;
	formattedType: string;
	billingPeriodStartTimestamp: number;
	formattedBillingPeriodStartDate: string;
	billingPeriodEndTimestamp: number;
	formattedBillingPeriodEndDate: string;
	formattedIssueDate: string;
	issueDateTimestamp: number;
	formattedDueDate: string;
	dueDateTimestamp: number;
	totalDue: number;
	formattedTotalDue: string;
	statuses:
		| PolicyInvoice2StatusAndTime[]
		| PremiumReportingInvoiceStatusAndTime[];
	status:
		| 'paid'
		| 'writtenOff'
		| 'zeroDue'
		| 'invalidated'
		| 'outstanding'
		| 'settled'
		| 'unfulfilled'
		| 'carriedForward';
	formattedStatus: string;
	settlementStatus: string;
	settlementType: string;
	invoiceType: string;
	latestPayment?: PaymentResponse2 | PremiumReportingPayment;
	documentUrl?: string;
	invoice: PolicyInvoice2Response | PremiumReportingInvoice;
	payments: PaymentResponse2[] | PremiumReportingPayment[];
}

const NewInvoicesTable = (props: Props) => {
	const urlParams = useParams();
	const queryClient = useQueryClient();
	const renewalData = useRenewalContext();
	const endorsementData = useEndorsementContext();
	const cancellationData = useCancellationContext();
	const premiumReportData = usePremiumReportContext();
	const policyData = usePolicyContext();
	const [isRetching, setIsRefetching] = React.useState(false);
	const {
		invoices,
		authData,
		currency,
		useInvoiceCurrency,
		policyholderLocator,
		isLoading,
	} = props;
	const hasPermission = authData.hasPermission;
	const authToken = authData?.auth?.authorizationToken;

	const [payInvoiceModalInfo, setPayInvoiceModalInfo] =
		React.useState<ModalInfo>(null);
	const [reverseInvoiceModalInfo, setReverseInvoiceModalInfo] =
		React.useState<ModalInfo>(null);
	const [writeOffInvoiceModal, setWriteOffInvoiceModal] =
		React.useState<ModalInfo>({ open: false, invoice: null });

	const writeOffInvoiceMutation = useWriteOffInvoiceMutation(
		authToken,
		queryClient,
	);

	const applyPaymentMutation = usePayInvoiceMutation(authToken, queryClient);

	const reverseInvoiceMutation = useReverseInvoiceMutation(
		authToken,
		queryClient,
	);

	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const refetchData = async (policyId?: string) => {
		setIsRefetching(true);
		const invoiceQueryKey = getUseInvoiceQueryKey(
			authToken,
			reverseInvoiceModalInfo?.invoice?.locator || '',
		);
		const invoicesForPolicyholderQueryKey = getInvoicesForPolicyholderQueryKey(
			authToken,
			policyholderLocator,
		);

		if (policyId) {
			const policyQueryKey = getUsePolicyQueryKey(policyId, authToken);
			queryClient.invalidateQueries(policyQueryKey);
		}

		queryClient.invalidateQueries(invoiceQueryKey);
		queryClient.invalidateQueries(invoicesForPolicyholderQueryKey);
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(policyId, authToken),
		);

		try {
			// TODO: refactor the following once using react-query to load all the data at upper level.
			if (urlParams.policyId) {
				if (urlParams.renewalId) {
					await renewalData.loadData(urlParams.policyId, urlParams.renewalId);
				}
				if (urlParams.endorsementId) {
					await endorsementData.loadData(
						urlParams.policyId,
						urlParams.endorsementId,
					);
				}
				if (urlParams.cancellationId) {
					await cancellationData.loadData(
						urlParams.policyId,
						urlParams.cancellationId,
					);
				}

				if (urlParams.premiumReportId) {
					await premiumReportData.fetchPremiumReport(urlParams.premiumReportId);
				}
				await policyData.loadData(urlParams.policyId);
			}
		} catch (error) {
			console.error(error);
		}
		setIsRefetching(false);
	};

	const writeOffInvoice = async (
		invoiceLocator: string,
		policyLocator: string,
	) => {
		await writeOffInvoiceMutation.mutateAsync({ invoiceLocator });
		refetchData(policyLocator);
	};

	const reversePayment = async (
		paymentLocator: string,
		policyLocator: string,
		reversalReason: PaymentReversalReason,
	) => {
		await reverseInvoiceMutation.mutateAsync({
			paymentLocator,
			reversalReason,
		});
		await refetchData(policyLocator);
	};

	const applyPayment = async (
		policyId: string,
		locator: string,
		paymentCreateReq: PaymentCreateRequest,
	) => {
		const paymentResponse = await applyPaymentMutation.mutateAsync({
			invoiceLocator: locator,
			paymentCreateReq,
		});
		refetchData(policyId);
		return paymentResponse.data;
	};

	const getRowActions = (params: GridRowParams<InvoiceRow>) => {
		const rowActions: JSX.Element[] = [];
		if (
			cashManagementEnabled &&
			// Premium reporting not yet working with cash management, so don't show this action if invoice is from a premium report
			params.row.invoiceType !== 'premiumReporting' &&
			params.row.settlementStatus === 'outstanding' &&
			hasPermission('invoice.pay')
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<AttachedMoneyIcon />}
					onClick={() =>
						setPayInvoiceModalInfo({ open: true, invoice: params.row.invoice })
					}
					label={INVOICES_TABLE__PAY_INVOICE_LABEL()}
					showInMenu
				/>,
			);
		}

		if (
			params.row.settlementStatus === 'settled' &&
			params.row.settlementType === 'paid' &&
			hasPermission('payment.reverse') &&
			cashManagementEnabled &&
			params.row.invoiceType !== 'premiumReporting'
		) {
			const latestPayment = params.row.latestPayment;
			if (latestPayment) {
				rowActions.push(
					<GridActionsCellItem
						icon={<HistoryIcon />}
						onClick={() =>
							setReverseInvoiceModalInfo({
								open: true,
								invoice: params.row.invoice,
							})
						}
						label={INVOICES_TABLE__REVERSE_PAYMENT_LABEL()}
						showInMenu
					/>,
				);
			}
		}

		const writtenOff = params.row.statuses.some(
			(status: PolicyInvoice2StatusAndTime) => status.status === 'writtenOff',
		);

		const outstanding = params.row.settlementStatus === 'outstanding';

		if (
			urlParams.policyId &&
			hasPermission('invoice.writeOff') &&
			!writtenOff &&
			outstanding
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<MoneyOffIcon />}
					onClick={() =>
						setWriteOffInvoiceModal({ open: true, invoice: params.row.invoice })
					}
					label={INVOICES_TABLE__WRITE_OFF_LABEL()}
					showInMenu
				/>,
			);
		}

		if (params.row.documentUrl) {
			rowActions.push(
				<GridActionsCellItem
					icon={<DownloadIcon />}
					onClick={() => {
						window.open(params.row.documentUrl, '_blank');
					}}
					label={INVOICES_TABLE__DOWNLOAD_INVOICE_LABEL()}
					showInMenu
				/>,
			);
		}

		rowActions.push(
			<GridActionsCellItem
				icon={<ContentCopyIcon />}
				onClick={() => {
					navigator.clipboard.writeText(params.row.locator);
					infoToast(INVOICES_TABLE__COPY_INVOICE_SUCCESS_TOAST());
				}}
				label={INVOICES_TABLE__COPY_INVOICE_LABEL()}
				showInMenu
			/>,
		);

		return rowActions;
	};

	const columns: GridColDef<InvoiceRow>[] = [
		{
			field: 'displayId',
			headerName: INVOICES_TABLE__INVOICE_ID_HEADER(),
			flex: 0.2,
			renderCell: (params) => (
				<LinkCell to={params.row.displayId} label={params.value} />
			),
		},
		{
			field: 'formattedType',
			headerName: INVOICES_TABLE__INVOICE_TYPE_HEADER(),
			flex: 0.2,
		},
		{
			field: 'billingPeriodStartTimestamp',
			headerName: INVOICES_TABLE__INVOICE_BILLING_PERIOD_HEADER(),
			flex: 0.2,
			renderCell: (params) => (
				<Stack spacing={0}>
					<Typography variant='body2'>{`${params.row.formattedBillingPeriodStartDate} -`}</Typography>
					<Typography variant='body2'>
						{params.row.formattedBillingPeriodEndDate}
					</Typography>
				</Stack>
			),
		},
		{
			field: 'issueDateTimestamp',
			headerName: INVOICES_TABLE__INVOICE_ISSUE_DATE_HEADER(),
			flex: 0.2,
			renderCell: (params) => params.row.formattedIssueDate,
		},
		{
			field: 'dueDateTimestamp',
			headerName: INVOICES_TABLE__INVOICE_DUE_DATE_HEADER(),
			flex: 0.2,
			renderCell: (params) => params.row.formattedDueDate,
		},
		{
			field: 'totalDue',
			headerName: INVOICES_TABLE__INVOICE_TOTAL_DUE_HEADER(),
			flex: 0.2,
			renderCell: (params) => params.row.formattedTotalDue,
		},
		{
			field: 'status',
			headerName: INVOICES_TABLE__INVOICE_STATUS_HEADER(),
			flex: 0.2,
			renderCell: (params) => <InvoiceStateChip state={params.row.status} />,
		},
		{
			field: 'actions',
			type: 'actions',
			align: 'right',
			flex: 0.2,
			sortable: false,
			getActions: getRowActions,
		},
	];

	const getFormattedAmount = (
		amount: string,
		invoiceCurrency: CurrencyCode,
	) => {
		return formatCurrency(
			parseFloat(amount),
			useInvoiceCurrency ? invoiceCurrency : currency,
		);
	};

	const filteredInvoices = (
		invoices: (PremiumReportingInvoice | PolicyInvoice2Response)[],
	): (PremiumReportingInvoice | PolicyInvoice2Response)[] => {
		return invoices?.filter((i) => i.settlementType !== 'invalidated');
	};

	const formattedData: InvoiceRow[] = filteredInvoices(invoices)?.map(
		(invoice: PolicyInvoice2Response | PremiumReportingInvoice) => {
			const formattedStartDate = authData.getZonedFormattedDateString(
				invoice.startTimestamp,
			);
			const formattedEndDate = authData.getZonedFormattedDateString(
				invoice.endTimestamp,
			);
			const formattedIssueDate = authData.getZonedFormattedDateString(
				invoice.createdTimestamp,
			);
			const formattedDueDate = authData.getZonedFormattedDateString(
				invoice.dueTimestamp,
			);
			const formattedTotalDue = getFormattedAmount(
				invoice.totalDue,
				invoice.totalDueCurrency,
			);
			const documentUrl = invoice.documents[0]?.url;
			const latestPayment =
				invoice.invoiceType === 'premiumReporting'
					? (invoice as PremiumReportingInvoice).payments
					: (invoice as PolicyInvoice2Response).payments.find(
							(p: PaymentResponse2) => !p.reversedTimestamp,
					  );
			const payments =
				invoice.invoiceType === 'premiumReporting'
					? [(invoice as PremiumReportingInvoice).payments]
					: (invoice as PolicyInvoice2Response).payments;
			const status =
				invoice.settlementStatus === 'settled'
					? invoice.settlementType
					: invoice.settlementStatus;
			const row: InvoiceRow = {
				locator: invoice.locator,
				displayId: invoice.displayId,
				type: invoice.invoiceType,
				formattedType:
					InvoiceTypeEnum[invoice.invoiceType as keyof typeof InvoiceTypeEnum],
				formattedBillingPeriodStartDate: formattedStartDate,
				formattedBillingPeriodEndDate: formattedEndDate,
				billingPeriodStartTimestamp: parseInt(invoice.startTimestamp),
				billingPeriodEndTimestamp: parseInt(invoice.endTimestamp),
				formattedIssueDate: formattedIssueDate,
				issueDateTimestamp: parseInt(invoice.createdTimestamp),
				formattedDueDate: formattedDueDate,
				dueDateTimestamp: parseInt(invoice.dueTimestamp),
				totalDue: parseFloat(invoice.totalDue),
				formattedTotalDue: formattedTotalDue,
				formattedStatus: formatInvoiceStatus(status),
				status: status,
				statuses: invoice.statuses,
				settlementStatus: invoice.settlementStatus,
				settlementType: invoice.settlementType,
				invoiceType: invoice.invoiceType,
				latestPayment: latestPayment,
				documentUrl: documentUrl,
				invoice: invoice,
				payments: payments,
			};
			return row;
		},
	);

	return (
		<>
			<GridWithToolbar
				rowId='locator'
				loading={isLoading}
				title={INVOICES_TABLE__HEADER()}
				columns={columns}
				data={formattedData}
			/>
			{payInvoiceModalInfo?.invoice && (
				<PayInvoiceModal
					isCallbackLoading={applyPaymentMutation.isLoading || isRetching}
					isCallbackSuccess={applyPaymentMutation.isSuccess}
					callback={applyPayment}
					open={payInvoiceModalInfo.open}
					setOpen={() => {
						setPayInvoiceModalInfo({
							open: false,
						});
					}}
					invoice={payInvoiceModalInfo.invoice}
				/>
			)}
			{reverseInvoiceModalInfo?.invoice && (
				<ReversePaymentModal
					callback={reversePayment}
					open={reverseInvoiceModalInfo.open}
					handleClose={() => {
						setReverseInvoiceModalInfo({
							open: false,
							invoice: null,
						});
					}}
					invoice={reverseInvoiceModalInfo?.invoice}
				/>
			)}
			{writeOffInvoiceModal?.invoice && (
				<WriteOffPaymentModal
					callback={writeOffInvoice}
					open={writeOffInvoiceModal.open}
					handleClose={() => {
						setWriteOffInvoiceModal({
							open: false,
							invoice: null,
						});
					}}
					invoice={writeOffInvoiceModal?.invoice}
				/>
			)}
		</>
	);
};

export default withAuthContext<Props>(NewInvoicesTable);
